/**
 * ForgeViewer Component
 * React wrapper for Autodesk Forge Viewer with proper lifecycle management
 */

import { authClient } from "@/api/clients/auth-client";
// MCP integration disabled - only needed for local development with Claude Code
// import { initializeMCPBridge } from "@/services/mcp-integration";
// import "@/services/mcp-test-utils"; // Load test utilities globally
import { useViewerStore } from "@/store/viewerStore";
import type {
  ViewerError,
  ViewerInitOptions,
  ViewerSelectionEvent,
} from "@/types/viewer";
import { useCallback, useEffect, useRef, useState } from "react";

interface ForgeViewerProps {
  modelUrn?: string;
  onViewerReady?: (viewer: Autodesk.Viewing.GuiViewer3D) => void;
  onModelLoaded?: () => void;
  onModelError?: (error: ViewerError) => void;
  onSelectionChanged?: (dbIds: number[]) => void;
}

export function ForgeViewer({
  modelUrn,
  onViewerReady,
  onModelLoaded,
  onModelError,
  onSelectionChanged,
}: ForgeViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Autodesk.Viewing.GuiViewer3D | null>(null);
  const [viewer, setViewer] = useState<Autodesk.Viewing.GuiViewer3D | null>(
    null
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>("");
  const { setViewer: setGlobalViewer, setModelLoaded, shouldAutoLoad, setShouldAutoLoad } = useViewerStore();

  // Prevent multiple model loading attempts
  const isLoadingRef = useRef(false);
  const lastLoadedUrnRef = useRef<string>("");

  // Initialize Forge Viewer
  const initializeViewer = useCallback(() => {
    if (!containerRef.current || isInitialized) return;

    setLoadingStatus("Initializing viewer...");

    const options: ViewerInitOptions = {
      env: "AutodeskProduction",
      api: "derivativeV2",
      // Add useADP: false to avoid deprecated endpoint warnings
      useADP: false,
      // Optionally disable some legacy features
      useCookie: false,
      getAccessToken: (callback: (token: string, expire: number) => void) => {
        authClient
          .getAccessToken()
          .then((token) => {
            callback(token, 3600);
          })
          .catch((error) => {
            callback("", 0);
          });
      },
    };

    Autodesk.Viewing.Initializer(options, async () => {
      try {
        const newViewer = new Autodesk.Viewing.GuiViewer3D(
          containerRef.current!
        );

        // Start the viewer
        await newViewer.start();

        // Skip WebGL validation to prevent errors - Forge Viewer handles this internally

        // Simplified camera handling - let Forge Viewer manage camera internally

        // Proceed with viewer setup immediately
        const completeViewerSetup = () => {
          viewerRef.current = newViewer;
          setViewer(newViewer); // Set state for React re-render
          setGlobalViewer(newViewer); // Set in global store for toolbar
          setIsInitialized(true);
          setLoadingStatus("Viewer ready");

          // Set up event listeners
          newViewer.addEventListener(
            Autodesk.Viewing.SELECTION_CHANGED_EVENT,
            (e: ViewerSelectionEvent) => {
              if (onSelectionChanged) {
                onSelectionChanged(e.dbIdArray || []);
              }
            }
          );

          // Handle ViewCube extension to prevent camera errors
          newViewer.addEventListener(
            Autodesk.Viewing.VIEWER_INITIALIZED,
            () => {}
          );

          if (onViewerReady) {
            onViewerReady(newViewer);
          }
        };

        // Proceed immediately with viewer setup
        completeViewerSetup();
        return; // Exit early, completeViewerSetup will handle the rest
      } catch (error) {
        setLoadingStatus("Viewer initialization failed");

        // Try to recover by reinitializing after a delay
        setTimeout(() => {
          setIsInitialized(false);
          // Use a fresh initialization instead of recursive call
          if (containerRef.current) {
            setLoadingStatus("Retrying viewer initialization...");
            // Reset state and try again
            setViewer(null);
            viewerRef.current = null;
            // The useEffect will trigger reinitialization
          }
        }, 2000);
      }
    });
  }, [isInitialized, onViewerReady, onSelectionChanged, setGlobalViewer]);

  // Load model when URN changes - but only if shouldAutoLoad is true
  useEffect(() => {
    // Skip if no URN or viewer not ready
    if (!modelUrn) {
      return;
    }

    // Only auto-load if explicitly requested (user clicked "Load Model")
    if (!shouldAutoLoad) {
      return;
    }

    if (!viewer) {
      return;
    }

    if (!isInitialized) {
      return;
    }

    // Prevent multiple loading attempts for the same URN
    if (isLoadingRef.current) {
      return;
    }

    // Prevent reloading the same URN
    if (lastLoadedUrnRef.current === modelUrn) {
      return;
    }

    // Set loading flag
    isLoadingRef.current = true;
    lastLoadedUrnRef.current = modelUrn;

    // Forge Viewer handles camera management internally
    setLoadingStatus("Loading model...");

    // Extract model loading logic to separate function for retry mechanism
    const loadModelInternal = () => {
      // Ensure the URN has the proper format
      let documentId = modelUrn;

      // If it doesn't start with 'urn:', add it
      if (!documentId.startsWith("urn:")) {
        documentId = `urn:${documentId}`;
      }

      console.log(
        "!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!Loading model...",
        documentId
      );
      Autodesk.Viewing.Document.load(
        documentId,
        (doc) => {
          // Get the default geometry (same approach as old code)
          const defaultViewable = doc.getRoot().getDefaultGeometry();

          if (!defaultViewable) {
            // Fallback to finding all viewables
            const viewables = doc.getRoot().findAllViewables();

            if (viewables.length === 0) {
              setLoadingStatus("No viewables found in model");
              if (onModelError) {
                onModelError(new Error("No viewables found"));
              }
              return;
            }

            const viewable = viewables.find((v) => v.is3D()) || viewables[0];
            setLoadingStatus("Loading geometry...");

            // Load the viewable
            viewer
              .loadDocumentNode(doc, viewable)
              .then(() => {
                setLoadingStatus("Waiting for model tree...");

                // Wait for object tree to be created before marking as loaded
                const onTreeReady = () => {
                  viewer.removeEventListener(
                    Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
                    onTreeReady
                  );
                  setLoadingStatus("Model loaded successfully");
                  setModelLoaded(true); // Update global store
                  isLoadingRef.current = false; // Reset loading flag
                  setShouldAutoLoad(false); // Reset auto-load flag after successful load

                  if (onModelLoaded) {
                    onModelLoaded();
                  }
                };

                // Check if tree already exists
                if (viewer.model && viewer.model.getInstanceTree()) {
                  onTreeReady();
                } else {
                  // Wait for tree to be created
                  viewer.addEventListener(
                    Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
                    onTreeReady
                  );
                }
              })
              .catch((error) => {
                setLoadingStatus("Failed to load geometry");
                isLoadingRef.current = false; // Reset loading flag
                setShouldAutoLoad(false); // Reset auto-load flag on error
                if (onModelError) {
                  onModelError(error);
                }
              });
            return;
          }

          setLoadingStatus("Loading geometry...");

          // Load the viewable into the viewer (use same approach as old code)
          viewer
            .loadDocumentNode(doc, defaultViewable)
            .then(() => {
              setLoadingStatus("Waiting for model tree...");

              // Wait for object tree to be created before marking as loaded
              const onTreeReady = () => {
                viewer.removeEventListener(
                  Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
                  onTreeReady
                );
                setLoadingStatus("Model loaded successfully");
                setModelLoaded(true); // Update global store
                isLoadingRef.current = false; // Reset loading flag
                setShouldAutoLoad(false); // Reset auto-load flag after successful load
                setShouldAutoLoad(false); // Reset auto-load flag after successful load

                if (onModelLoaded) {
                  onModelLoaded();
                }
              };

              // Check if tree already exists
              if (viewer.model && viewer.model.getInstanceTree()) {
                onTreeReady();
              } else {
                // Wait for tree to be created
                viewer.addEventListener(
                  Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
                  onTreeReady
                );
              }
            })
            .catch((error) => {
              setLoadingStatus("Failed to load geometry");
              isLoadingRef.current = false; // Reset loading flag
              setShouldAutoLoad(false); // Reset auto-load flag on error
              if (onModelError) {
                onModelError(error);
              }
            });
        },
        (errorCode) => {
          let errorMessage = "Failed to load document";

          // Common Forge error codes
          switch (errorCode) {
            case 1:
              errorMessage = "Network error - check your connection";
              break;
            case 4:
              errorMessage = "Model not found - check URN";
              break;
            case 5:
              errorMessage = "Invalid URN format";
              break;
            case 6:
              errorMessage = "Translation in progress - try again later";
              break;
            case 7:
              errorMessage = "Translation failed";
              break;
            case 9:
              errorMessage = "Authentication failed - refresh token";
              break;
            case 13:
              errorMessage = "Model does not exist or is not translated";
              break;
            default:
              errorMessage = `Error code: ${errorCode}`;
          }

          setLoadingStatus(errorMessage);
          isLoadingRef.current = false; // Reset loading flag
          setShouldAutoLoad(false); // Reset auto-load flag on error
          if (onModelError) {
            onModelError(new Error(errorMessage));
          }
        }
      );
    };

    loadModelInternal();
  }, [modelUrn, viewer, isInitialized, onModelLoaded, onModelError, shouldAutoLoad, setShouldAutoLoad]);

  // Initialize viewer on mount
  useEffect(() => {
    // Wait for Autodesk namespace to be available
    const checkAutodesk = setInterval(() => {
      if (window.Autodesk) {
        clearInterval(checkAutodesk);
        initializeViewer();
      }
    }, 100);

    return () => {
      clearInterval(checkAutodesk);
      // Only cleanup on unmount, not when viewer changes
      if (viewerRef.current) {
        viewerRef.current.tearDown();
        viewerRef.current = null;
      }
    };
  }, [initializeViewer]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (viewerRef.current) {
        viewerRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />

      {/* Custom Message Overlay - Shows when no model is loaded */}
      {!modelUrn && (
        <div className="absolute inset-0 flex items-center justify-center bg-dark-200 z-50">
          <div className="text-center px-8">
            <div className="mb-8">
            </div>
            <h2 className="text-4xl font-bold text-gray-100 mb-3">
              Design with confidence
            </h2>
            <h2 className="text-4xl font-bold text-gray-100">
              Build without penalties
            </h2>
            <p className="text-gray-400 mt-8 text-lg">
              Upload a model to begin
            </p>
          </div>
        </div>
      )}

      {/* Loading Status Overlay */}
      {loadingStatus && modelUrn && (
        <div className="absolute top-4 right-4 bg-dark-100 px-4 py-2 rounded-lg shadow-lg border border-gray-700">
          <div className="flex items-center gap-2">
            {loadingStatus.includes("Loading") && (
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            )}
            <span className="text-sm text-gray-300">{loadingStatus}</span>
          </div>
        </div>
      )}
    </div>
  );
}
